import { Loader2, Sparkles, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { MarkdownPreview } from '@/components/markdown/MarkdownEditor';
import { replacePassage, type Passage } from '@/components/markdown/markdownEditing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export type SkillAssistRequest =
  | { mode: 'draft'; name: string; description: string; instruction: string }
  | { mode: 'improve'; name: string; description: string; body: string; selection: string; instruction: string };

type Panel =
  | { kind: 'draft'; instruction: string; suggestion?: string }
  | { kind: 'improve'; passage: Passage; instruction: string; suggestion?: string };

const QUICK_EDITS = ['shorter', 'clearer', 'specific'] as const;
const QUICK_LABELS: Record<(typeof QUICK_EDITS)[number], string> = { shorter: 'Make it shorter', clearer: 'Make it clearer', specific: 'Make it more specific' };

/**
 * AI help that only proposes: a first draft for an empty skill, or a new
 * version of the passage someone selected. Nothing changes until the person
 * picks Use draft or Replace. Studio's Settings has the same, translated.
 */
export function useSkillAssist({
  name,
  description,
  body,
  selection,
  onChangeBody,
  request,
}: {
  name: string;
  description: string;
  body: string;
  selection: { start: number; end: number };
  onChangeBody: (body: string) => void;
  request: (input: SkillAssistRequest) => Promise<string>;
}) {
  const [panel, setPanel] = useState<Panel | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const selected = body.slice(selection.start, selection.end);
  const drafting = !body.trim();

  const ask = async (next: Panel) => {
    setBusy(true);
    setError(undefined);
    try {
      const suggestion = await request(
        next.kind === 'draft'
          ? { mode: 'draft', name, description, instruction: next.instruction }
          : { mode: 'improve', name, description, body, selection: next.passage.text, instruction: next.instruction },
      );
      setPanel({ ...next, suggestion });
    } catch (err) {
      setPanel(next);
      setError(err instanceof Error && err.message ? err.message : 'Could not get a suggestion. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const start = () => {
    setError(undefined);
    if (drafting) {
      const draft: Panel = { kind: 'draft', instruction: description.trim() };
      setPanel(draft);
      if (draft.instruction) void ask(draft);
      return;
    }
    if (selected.trim()) setPanel({ kind: 'improve', passage: { ...selection, text: selected }, instruction: '' });
  };

  const accept = () => {
    if (!panel?.suggestion) return;
    if (panel.kind === 'draft') {
      onChangeBody(panel.suggestion);
      setPanel(null);
      return;
    }
    const next = replacePassage(body, panel.passage, panel.suggestion);
    if (next === null) {
      setError('That text changed since you selected it. Select it again.');
      return;
    }
    onChangeBody(next);
    setPanel(null);
  };

  const toolbarButton = (
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={start}
      disabled={busy || (!drafting && !selected.trim())}
      title={drafting ? 'Write a first draft from “When to use it”' : selected.trim() ? 'Suggest a new version of the selected text' : 'Select text in the instructions to improve it'}
      className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-background hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
    >
      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
      {drafting ? 'Draft with AI' : 'Improve selection'}
    </button>
  );

  const submitInstruction = (event: FormEvent) => {
    event.preventDefault();
    if (panel && panel.instruction.trim() && !busy) void ask(panel);
  };

  const panelView = panel ? (
    <div className="rounded-xl border border-border bg-card/60 p-3">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-xs text-muted-foreground">
          {panel.kind === 'draft' ? 'A first draft to edit. Nothing changes until you use it.' : 'Only the selected text changes, and only if you replace it.'}
        </p>
        <button
          type="button"
          aria-label={'Close'}
          onClick={() => setPanel(null)}
          className="rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      {panel.suggestion === undefined ? (
        <form onSubmit={submitInstruction} className="mt-2.5 flex flex-col gap-2">
          <div className="flex gap-2">
            <Input
              autoFocus
              value={panel.instruction}
              onChange={(event) => setPanel({ ...panel, instruction: event.target.value })}
              placeholder={panel.kind === 'draft' ? 'What should the agent do? For example: screens for dispatchers on a wall display' : 'How should it change?'}
              disabled={busy}
              className="h-8 text-sm"
            />
            <Button type="submit" size="sm" className="h-8" disabled={busy || !panel.instruction.trim()}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : 'Suggest'}
            </Button>
          </div>
          {panel.kind === 'improve' && !busy ? (
            <div className="flex flex-wrap gap-1.5">
              {QUICK_EDITS.map((quick) => (
                <button
                  key={quick}
                  type="button"
                  onClick={() => void ask({ ...panel, instruction: QUICK_LABELS[quick] })}
                  className="rounded-full border border-border px-2.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {QUICK_LABELS[quick]}
                </button>
              ))}
            </div>
          ) : null}
        </form>
      ) : (
        <div className="mt-2.5 space-y-2.5">
          <div className="max-h-72 overflow-auto rounded-lg border border-border bg-background px-3 py-2">
            <MarkdownPreview markdown={panel.suggestion} />
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" className="h-8" onClick={accept}>
              {panel.kind === 'draft' ? 'Use draft' : 'Replace selection'}
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-8" disabled={busy} onClick={() => void ask(panel)}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : 'Try again'}
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => setPanel(null)}>
              {'Discard'}
            </Button>
          </div>
        </div>
      )}
      {busy && panel.suggestion === undefined && panel.kind === 'draft' && panel.instruction ? (
        <p className="mt-2 text-xs text-muted-foreground">{'Writing a draft…'}</p>
      ) : null}
      {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
    </div>
  ) : null;

  return { toolbarButton, panel: panelView };
}
